/**
 * `user_plan` person-property sync decision (platform-agnostic).
 *
 * The app mirrors the derived plan ('free' | 'premium') to PostHog two ways:
 * a super-property (local, no event, registered every time) and a `$set`
 * person property (one event). The `$set` must be sent only when the
 * (identity, plan) pair actually changes, and only when it can land:
 *
 *  - not before the subscription has been derived for this launch:
 *    `isSubscribe` starts false, so an early send stamps a premium user as
 *    'free' and then again as 'premium' on every cold start (two events, one
 *    transiently wrong);
 *  - not while anonymous: PostHog (identified_only mode) discards `$set` on
 *    personless events, so the send is wasted;
 *  - not when the last persisted stamp already equals `distinctId|plan`.
 *
 * The stamp is keyed by distinct_id so sign-in / sign-out re-sends for the new
 * identity while a cold start with the same identity and plan does not.
 */

export const USER_PLANS = ['free', 'premium'];

/** @returns {string} the persisted stamp for an (identity, plan) pair */
export function buildUserPlanStamp(distinctId, plan) {
  return `${distinctId}|${plan}`;
}

/**
 * @param {{
 *   lastStamp: string|null|undefined,   // persisted stamp from the previous send
 *   distinctId: unknown,                // posthog.getDistinctId() after ready()
 *   plan: unknown,                      // 'free' | 'premium'
 *   isIdentified: boolean,
 *   subscriptionResolved: boolean,      // NewAuthProvider has derived the plan this launch
 * }} s
 * @returns {{ action: 'send'|'skip', reason: string, stamp: string|null }}
 */
export function resolveUserPlanSetAction({ lastStamp, distinctId, plan, isIdentified, subscriptionResolved }) {
  if (!USER_PLANS.includes(plan)) return { action: 'skip', reason: 'invalid_plan', stamp: null };
  if (typeof distinctId !== 'string' || distinctId.trim() === '') return { action: 'skip', reason: 'not_ready', stamp: null };
  const stamp = buildUserPlanStamp(distinctId, plan);
  if (!subscriptionResolved) return { action: 'skip', reason: 'unresolved', stamp };
  if (!isIdentified) return { action: 'skip', reason: 'anonymous', stamp };
  if (lastStamp === stamp) return { action: 'skip', reason: 'unchanged', stamp };
  return { action: 'send', reason: 'changed', stamp };
}
